package er.prateek.crossly.ui.screens.brainstorm

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.brainstorm_continue
import crossly.shared.generated.resources.brainstorm_end
import crossly.shared.generated.resources.brainstorm_kicker
import crossly.shared.generated.resources.brainstorm_next_round
import crossly.shared.generated.resources.brainstorm_nice
import crossly.shared.generated.resources.brainstorm_round_done
import crossly.shared.generated.resources.brainstorm_round_done_body
import crossly.shared.generated.resources.brainstorm_up_next
import er.prateek.crossly.ads.AdTrigger
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.di.AppContainer
import er.prateek.crossly.progression.BrainstormSession
import er.prateek.crossly.ui.components.ConfettiBurst
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonSize
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.GameCard
import er.prateek.crossly.ui.components.GameScreen
import er.prateek.crossly.ui.components.IconCheck
import er.prateek.crossly.ui.components.IconPlay
import er.prateek.crossly.ui.components.ModeBadge
import er.prateek.crossly.ui.components.PlayMode
import er.prateek.crossly.ui.components.StarRating
import er.prateek.crossly.ui.components.tint
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.stringResource

/** Matches the results screens' reward beat, so an ad never lands on the stars. */
private const val INTERSTITIAL_DELAY_MS = 1_400L

/**
 * The breather between Brainstorm games: stars for the game just played, where the player is in
 * the Alphabets -> Maths -> Riddles round, and what's next. Unlocked badges celebrate here --
 * never over a live, timed board.
 */
@Composable
fun BrainstormBreakScreen(
    completedMode: GameMode,
    stars: Int,
    roundComplete: Boolean,
    onContinue: () -> Unit,
    onEnd: () -> Unit,
) {
    val colors = MaterialTheme.game
    val upcoming = PlayMode.of(BrainstormSession.upcomingMode)
    val shownRound = if (roundComplete) BrainstormSession.round - 1 else BrainstormSession.round
    // Games done in the round being shown: all three on a round finish, else the session position.
    val doneInRound = if (roundComplete) BrainstormSession.order.size else BrainstormSession.position

    // A run skips the results screens where interstitials normally fire; `modes.brainstorm.
    // show_interstitials` puts that checkpoint here instead, on the same shared pacing. After the
    // reward beat, so an ad never lands on the stars.
    LaunchedEffect(Unit) {
        if (!AppContainer.appConfig().modes.brainstorm.showInterstitials) return@LaunchedEffect
        delay(INTERSTITIAL_DELAY_MS)
        AppContainer.adsCoordinator.onPuzzleCompleted(
            when (completedMode) {
                GameMode.CROSSWORD -> AdTrigger.PUZZLE_COMPLETED
                GameMode.MATH_CROSSWORD -> AdTrigger.MATH_COMPLETED
                GameMode.RIDDLES -> AdTrigger.RIDDLE_COMPLETED
            },
        )
    }

    GameScreen(
        overlay = { if (roundComplete) ConfettiBurst(seed = shownRound) },
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(Spacing.lg, Alignment.CenterVertically),
        ) {
            Text(
                text = stringResource(Res.string.brainstorm_kicker, shownRound),
                style = MaterialTheme.typography.labelLarge.copy(letterSpacing = 2.sp, fontWeight = FontWeight.Black),
                color = colors.flame,
            )
            Text(
                text = stringResource(if (roundComplete) Res.string.brainstorm_round_done else Res.string.brainstorm_nice),
                style = MaterialTheme.typography.displaySmall,
                color = colors.ink,
                textAlign = TextAlign.Center,
            )
            StarRating(stars = stars, starSize = 40.dp, spacing = Spacing.sm, animated = true)
            if (roundComplete) {
                Text(
                    text = stringResource(Res.string.brainstorm_round_done_body),
                    style = MaterialTheme.typography.bodyMedium,
                    color = colors.inkMuted,
                    textAlign = TextAlign.Center,
                )
            }

            RoundTrack(
                doneCount = doneInRound,
                justFinished = PlayMode.of(completedMode),
                modifier = Modifier.widthIn(max = 420.dp).fillMaxWidth().padding(vertical = Spacing.md),
            )

            GameCard(
                modifier = Modifier.widthIn(max = 420.dp).fillMaxWidth(),
                accentGlow = upcoming.tint().copy(alpha = 0.5f),
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(Spacing.md)) {
                    ModeBadge(upcoming, size = 56.dp)
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = stringResource(Res.string.brainstorm_up_next).uppercase(),
                            style = MaterialTheme.typography.labelSmall.copy(letterSpacing = 1.5.sp),
                            color = colors.inkMuted,
                        )
                        Text(text = stringResource(upcoming.title), style = MaterialTheme.typography.titleLarge, color = colors.ink)
                        Text(text = stringResource(upcoming.subtitle), style = MaterialTheme.typography.bodySmall, color = colors.inkMuted)
                    }
                }
            }

            Column(
                modifier = Modifier.widthIn(max = 420.dp).fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(Spacing.sm),
            ) {
                GameButton(
                    text = stringResource(if (roundComplete) Res.string.brainstorm_next_round else Res.string.brainstorm_continue),
                    onClick = onContinue,
                    style = GameButtonStyle.PRIMARY,
                    size = GameButtonSize.LARGE,
                    fillWidth = true,
                    icon = { tint -> IconPlay(size = 18.dp, tint = tint) },
                )
                GameButton(
                    text = stringResource(Res.string.brainstorm_end),
                    onClick = onEnd,
                    style = GameButtonStyle.GHOST,
                    fillWidth = true,
                )
            }
        }
    }
}

/** Three stations joined by a rail: done ones checked in their mode colour, the next one breathing. */
@Composable
private fun RoundTrack(doneCount: Int, justFinished: PlayMode, modifier: Modifier = Modifier) {
    val colors = MaterialTheme.game
    val stations = BrainstormSession.order.map { PlayMode.of(it) }
    val transition = rememberInfiniteTransition(label = "roundTrack")
    val breathe by transition.animateFloat(
        initialValue = 0.9f,
        targetValue = 1.1f,
        animationSpec = infiniteRepeatable(tween(800), RepeatMode.Reverse),
        label = "nextStation",
    )
    Row(modifier = modifier, verticalAlignment = Alignment.Top) {
        stations.forEachIndexed { index, mode ->
            val done = index < doneCount
            val isNext = index == doneCount
            Column(
                modifier = Modifier.weight(1f),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(Spacing.xs),
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Rail(visible = index > 0, lit = done, modifier = Modifier.weight(1f))
                    Box(
                        modifier = Modifier
                            .size(44.dp)
                            .graphicsLayer {
                                val s = if (isNext) breathe else if (mode == justFinished && done) 1.08f else 1f
                                scaleX = s; scaleY = s
                            }
                            .then(
                                if (done) {
                                    Modifier.background(mode.tint(), CircleShape)
                                } else {
                                    Modifier.border(2.dp, if (isNext) mode.tint() else colors.locked, CircleShape)
                                },
                            ),
                        contentAlignment = Alignment.Center,
                    ) {
                        if (done) {
                            IconCheck(size = 20.dp, tint = colors.onAccent)
                        } else {
                            Text(text = mode.glyph, style = MaterialTheme.typography.titleMedium, color = if (isNext) mode.tint() else colors.inkMuted)
                        }
                    }
                    Rail(visible = index < stations.lastIndex, lit = index + 1 < doneCount, modifier = Modifier.weight(1f))
                }
                Text(
                    text = stringResource(mode.title),
                    style = MaterialTheme.typography.labelSmall,
                    color = if (done || isNext) colors.ink else colors.inkMuted,
                    maxLines = 1,
                )
            }
        }
    }
}

@Composable
private fun Rail(visible: Boolean, lit: Boolean, modifier: Modifier) {
    val colors = MaterialTheme.game
    Box(
        modifier = modifier
            .height(4.dp)
            .then(if (visible) Modifier.background(if (lit) colors.success else colors.locked.copy(alpha = 0.4f)) else Modifier),
    )
}
